/**
 * UserManagement Component
 * Manages users, portal members, and invitations for an organization
 *
 * MIGRATED: Custom CSS → Tailwind CSS (Mobile-First & Desktop-Ready)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Users, Mail, UserPlus, Trash2, Shield, Clock, CheckCircle, XCircle, Settings, Search, UserCircle, RefreshCw, KeyRound, Power, Pencil, ArrowRight, Eye, Send, PauseCircle, Activity, AlertTriangle, Loader2 } from 'lucide-react';
import AccessActivityReport from './AccessActivityReport';
import { ViewAsUserModal, type ViewAsTarget } from '@/components/impersonation/ViewAsUserModal';
import { ImpersonationLog } from '@/components/impersonation/ImpersonationLog';
import { trackAction } from '@/src/lib/activityTracker';
import { userManagementAPI, OrganizationUser, UserInvitation, USER_ROLES } from '@/services/api/userManagement';
import type { BulkInviteResult } from '@/services/api/userManagement';
import { getWeaveinApiUrl } from '@/src/config';
import { UserSetupWizard } from './UserSetupWizard';
import { SuperAdminApprovals } from './SuperAdminApprovals';
import { isInternalSystemRole } from '@/utils/superAdminPolicy';
import BulkReassignDialog from './BulkReassignDialog';
import { UserAssignments } from './UserAssignments';
import { ManageUserRolesModal } from './ManageUserRolesModal';
import TrivenyRoleChangeModal from './TrivenyRoleChangeModal';
import { cn } from '@/lib/utils';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { config as productConfig } from '@/src/config';

// Search input — defined outside the component so React keeps a stable reference
const SearchInput = ({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) => (
  <div className="relative mb-4">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
    />
    {value && (
      <button
        onClick={() => onChange('')}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        <XCircle className="w-4 h-4" />
      </button>
    )}
  </div>
);

// The bulk-invite API reports a machine code per person it refuses. An admin
// reading "why didn't Ana get one?" needs the sentence, not the enum — and in
// their own language, so these resolve through t() like any other copy.
const SKIP_REASON_KEYS: Record<string, [string, string]> = {
  already_claimed: ['admin.skip_already_claimed', 'Already set up — has signed in or has a password'],
  invitation_already_pending: ['admin.skip_invitation_pending', 'Already has a pending invitation'],
  portal_member: ['admin.skip_portal_member', 'Portal member — invited from the Members area'],
  platform_admin_role: ['admin.skip_platform_admin', 'Platform admin — not invited per organization'],
  inactive: ['admin.skip_inactive', 'Deactivated — reactivate first'],
  no_email: ['admin.skip_no_email', 'No email address on file'],
  not_in_organization: ['admin.skip_not_in_org', 'Not a member of this organization'],
};

interface UserManagementProps {
  cboId: number;
  /**
   * The active org id WITHOUT the `|| 1` fallback: null means "not resolved
   * yet". Only the super-admin privilege gate uses it — everything else keeps
   * using `cboId`. See TRUS-513 / TRUS-435.
   */
  resolvedOrgId?: number | null;
  organizationName: string;
  currentUserRole: string;
  onNavigate?: (tab: string, context?: any) => void;
}

export const UserManagement: React.FC<UserManagementProps> = ({
  cboId,
  resolvedOrgId,
  organizationName,
  currentUserRole,
  onNavigate,
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [allUsers, setAllUsers] = useState<OrganizationUser[]>([]);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showOrgActivity, setShowOrgActivity] = useState(false);
  const [showBulkReassign, setShowBulkReassign] = useState(false);

  // Bulk invite: selecting unclaimed staff accounts and notifying them.
  //
  // `selectedUserIds` holds users.id values only — never the synthetic negative
  // ids the list uses for pending-invitation rows, which is why the checkbox is
  // not rendered for them at all.
  //
  // The list has no password column, so the UI can only guess who is invitable
  // (last_login === null). It does not have to be right: the dry run is the
  // authority and reports anyone it refuses, with a reason. Nothing is sent
  // until the preview comes back and the admin confirms it.
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [bulkInvitePreview, setBulkInvitePreview] = useState<BulkInviteResult | null>(null);
  const [bulkInviteLoading, setBulkInviteLoading] = useState(false);
  const [bulkInviteSending, setBulkInviteSending] = useState(false);
  // TRUS-530: the org has not resolved yet. `cboId` is `activeOrgId || 1` at the
  // call site, so while activeOrgId is null every write on this screen is aimed
  // at CBO 1 — Trust Care Organization, the platform org — no matter which
  // workspace the admin thinks they are in. TRUS-435 makes that window real:
  // active_cbo_id is deliberately left unset for a super_admin whose JWT has no
  // cbo_id, until OrganizationContext resolves their org and writes it.
  //
  // Reading the wrong org is recoverable; INVITING into it is not, so the invite
  // flow is held until we actually know where we are. The wait is one API call
  // (OrganizationContext.fetchOrganizations), not an indefinite state — unless
  // that fetch failed, which is why the banner offers a reload.
  const orgUnresolved = resolvedOrgId === null || resolvedOrgId === undefined;

  const [activeTab, setActiveTab] = useState<'users' | 'portal' | 'invitations' | 'platform-admins' | 'view-as-log'>('users');
  const [selectedUserForAssignments, setSelectedUserForAssignments] = useState<OrganizationUser | null>(null);
  const [selectedUserForRoles, setSelectedUserForRoles] = useState<OrganizationUser | null>(null);
  const [selectedUserForActivity, setSelectedUserForActivity] = useState<OrganizationUser | null>(null);
  // TRUS-747: super admin "View as user".
  const [viewAsTarget, setViewAsTarget] = useState<ViewAsTarget | null>(null);
  // Platform Admins roster (super_admin-only tab) — global, org-independent.
  const [platformAdmins, setPlatformAdmins] = useState<any[]>([]);
  const [loadingPlatformAdmins, setLoadingPlatformAdmins] = useState(false);
  // Account-wide removal confirmation. When the target holds an account-level
  // role (super_admin/ops_admin), the backend 409s with the list of orgs that
  // would be revoked; we surface them here and retry with confirm_account_wide.
  const [accountWideRemoval, setAccountWideRemoval] = useState<{
    userId: number;
    userName: string;
    userType: 'user' | 'member';
    message: string;
    affectedOrganizations: Array<{ cbo_id: number; name: string | null; role: string }>;
  } | null>(null);
  const [accountWideRemoving, setAccountWideRemoving] = useState(false);

  // Edit user modal state
  const [editingUser, setEditingUser] = useState<OrganizationUser | null>(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editEmailEnabled, setEditEmailEnabled] = useState(false);
  const [editEmailReason, setEditEmailReason] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  // Per-user 2FA management (accepted users only). editTwoFARequired is the
  // toggle value; editTwoFAInitial holds the loaded value so we only PUT on a
  // real change. null = still loading (or not applicable, e.g. invited rows).
  const [editTwoFARequired, setEditTwoFARequired] = useState(false);
  const [editTwoFAInitial, setEditTwoFAInitial] = useState<boolean | null>(null);
  const [editTwoFAMethod, setEditTwoFAMethod] = useState<'sms' | 'totp'>('sms');
  const [editTwoFAPhoneVerified, setEditTwoFAPhoneVerified] = useState(false);

  // Edit-invitation modal state (full edit: name / phone / email / role / 2FA)
  const [editingInvitation, setEditingInvitation] = useState<UserInvitation | null>(null);
  const [editInvRole, setEditInvRole] = useState('');
  const [editInvName, setEditInvName] = useState('');
  const [editInvPhone, setEditInvPhone] = useState('');
  const [editInvEmail, setEditInvEmail] = useState('');
  const [editInvRequire2FA, setEditInvRequire2FA] = useState(true);
  const [editInvSaving, setEditInvSaving] = useState(false);
  const [editInvError, setEditInvError] = useState<string | null>(null);
  // TRUS-186: org roles (role_terminology) for the edit-invitation dropdown so
  // admins can re-assign to a custom org role (e.g. "Case Manager"), not just
  // the hardcoded system roles. WeaveIn only — Triveny keeps the platform-role
  // keys sourced from USER_ROLES.
  const [orgRoles, setOrgRoles] = useState<
    { role_type: string; display_name_en: string; system_role: string }[]
  >([]);

  // Search states for each tab
  const [userSearch, setUserSearch] = useState('');
  const [portalSearch, setPortalSearch] = useState('');
  const [invitationSearch, setInvitationSearch] = useState('');

  // Triveny tenants: the backend bridges invite / list-invitations /
  // cancel / resend to platform_shared.tenant_invitations and lists users
  // from platform_shared.tenant_users. Role assignment goes through
  // /api/platform/roles (Triveny-specific modal); Edit + Delete still use
  // the WeaveIn user-table path so they stay disabled on Triveny.
  const isTriveny = productConfig.isTriveny;
  // TRUS-747: only super admins may open the app as another user.
  const canViewAsUsers = currentUserRole === 'super_admin' && !isTriveny;
  const isTrivenyAdminRole = ['platform_admin', 'tenant_admin', 'super_admin', 'org_admin', 'ops_admin'].includes(currentUserRole);
  const canInviteUsers = isTriveny
    ? isTrivenyAdminRole
    : ['super_admin', 'org_admin', 'ops_admin'].includes(currentUserRole);
  const canDeleteUsers = !isTriveny && ['super_admin', 'org_admin', 'ops_admin'].includes(currentUserRole);
  // Invitation actions (send held / resend / cancel / edit role) and user
  // detail edits (phone / name) work on Triveny too — the backend bridges
  // are wired. We don't conflate these with delete (which still needs the
  // healthcare user-table path).
  const canManageInvitations = isTriveny
    ? isTrivenyAdminRole
    : ['super_admin', 'org_admin', 'ops_admin'].includes(currentUserRole);
  const canEditUserDetails = canManageInvitations;
  const canManageAssignments = isTriveny
    ? isTrivenyAdminRole
    : ['super_admin', 'org_admin', 'manager'].includes(currentUserRole);
  // Triveny role assignments go to a different modal that talks to
  // /api/platform/roles instead of the WeaveIn role_terminology API.
  const [selectedTrivenyUserForRoles, setSelectedTrivenyUserForRoles] = useState<OrganizationUser | null>(null);

  // Split users into staff and portal members
  const staffUsers = useMemo(() =>
    allUsers.filter(u => u.user_type !== 'member'),
    [allUsers]
  );

  const portalUsers = useMemo(() =>
    allUsers.filter(u => u.user_type === 'member'),
    [allUsers]
  );

  // Filtered lists based on search
  const filteredStaffUsers = useMemo(() => {
    if (!userSearch.trim()) return staffUsers;
    const search = userSearch.toLowerCase();
    return staffUsers.filter(u =>
      (u.name || '').toLowerCase().includes(search) ||
      (u.email || '').toLowerCase().includes(search) ||
      (u.user_org_role || '').toLowerCase().includes(search)
    );
  }, [staffUsers, userSearch]);

  const filteredPortalUsers = useMemo(() => {
    if (!portalSearch.trim()) return portalUsers;
    const search = portalSearch.toLowerCase();
    return portalUsers.filter(u =>
      (u.name || '').toLowerCase().includes(search) ||
      (u.email || '').toLowerCase().includes(search)
    );
  }, [portalUsers, portalSearch]);

  const filteredInvitations = useMemo(() => {
    if (!invitationSearch.trim()) return invitations;
    const search = invitationSearch.toLowerCase();
    return invitations.filter(i =>
      (i.name || '').toLowerCase().includes(search) ||
      (i.email || '').toLowerCase().includes(search) ||
      (i.role || '').toLowerCase().includes(search) ||
      (i.status || '').toLowerCase().includes(search)
    );
  }, [invitations, invitationSearch]);

  useEffect(() => {
    loadData();
    // Drop any selection on an org switch. Carrying user ids across tenants
    // would aim the next "Invite Selected" at the wrong organization.
    setSelectedUserIds(new Set());
    setBulkInvitePreview(null);
  }, [cboId]);

  // TRUS-186: load the org's role_terminology roles so the edit-invitation
  // dropdown can offer real org roles (WeaveIn only). Triveny role keys come
  // from USER_ROLES, so skip the healthcare /all-roles fetch there.
  useEffect(() => {
    if (isTriveny || !canManageInvitations) return;
    let cancelled = false;
    userManagementAPI
      .getAllOrgRoles(cboId)
      .then((roles) => {
        if (!cancelled) setOrgRoles(roles || []);
      })
      .catch((err) => {
        // Non-fatal: the dropdown falls back to the USER_ROLES system roles.
        console.error('TRUS-186: failed to load org roles for invite edit:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [cboId, isTriveny, canManageInvitations]);

  // Load the platform-admin roster when its (super_admin-only) tab is opened.
  useEffect(() => {
    if (activeTab !== 'platform-admins' || currentUserRole !== 'super_admin') return;
    let cancelled = false;
    (async () => {
      setLoadingPlatformAdmins(true);
      try {
        const res = await fetch(`${getWeaveinApiUrl()}/api/admin/access-report/platform-admins?days=90`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            ...(cboId ? { 'X-CBO-ID': String(cboId) } : {}),
          },
        });
        if (res.ok && !cancelled) {
          const d = await res.json();
          setPlatformAdmins(d.admins || []);
        }
      } catch {
        /* non-blocking */
      } finally {
        if (!cancelled) setLoadingPlatformAdmins(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, currentUserRole, cboId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersData, invitationsData] = await Promise.all([
        userManagementAPI.listUsers(cboId),
        canInviteUsers ? userManagementAPI.listInvitations(cboId) : Promise.resolve([])
      ]);

      setAllUsers(usersData);
      setInvitations(invitationsData);
    } catch (err: any) {
      console.error('👥 UserManagement - Failed to load data:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        message: err.response?.data?.detail || err.message,
        url: err.config?.url,
        method: err.config?.method?.toUpperCase(),
        cboId,
        hasToken: !!localStorage.getItem('auth_token'),
      });
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId: number, userName: string, userType: 'user' | 'member' = 'user') => {
    const entityType = userType === 'member' ? 'member' : 'user';
    if (!confirm(`Are you sure you want to remove ${userName} from this organization? This will permanently delete this ${entityType}'s access.`)) {
      return;
    }

    try {
      await userManagementAPI.removeUser(cboId, userId, userType);
      await loadData();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      // Account-level user: the backend blocks the wipe until we confirm it
      // removes access to ALL orgs. Open the confirmation modal instead of the
      // generic alert (detail is an object here, not a string).
      if (err.response?.status === 409 && detail?.requires_confirmation) {
        setAccountWideRemoval({
          userId,
          userName,
          userType,
          message: detail.message || `${userName} will be removed from all organizations.`,
          affectedOrganizations: detail.affected_organizations || [],
        });
        return;
      }
      alert(typeof detail === 'string' ? detail : `Failed to remove ${entityType}`);
      await loadData();
    }
  };

  const confirmAccountWideRemoval = async () => {
    if (!accountWideRemoval) return;
    const { userId, userType } = accountWideRemoval;
    setAccountWideRemoving(true);
    try {
      await userManagementAPI.removeUser(cboId, userId, userType, true);
      setAccountWideRemoval(null);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to remove user from all organizations');
    } finally {
      setAccountWideRemoving(false);
      await loadData();
    }
  };

  const handleCancelInvitation = async (invitationId: number, email: string, invitationType: 'user' | 'member' = 'user') => {
    if (!confirm(`Are you sure you want to cancel the invitation to ${email}?`)) {
      return;
    }

    try {
      await userManagementAPI.cancelInvitation(cboId, invitationId, invitationType);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  const handleResendInvitation = async (invitationId: number, email: string, invitationType: 'user' | 'member' = 'user') => {
    try {
      await userManagementAPI.resendInvitation(cboId, invitationId, invitationType);
      alert(`Invitation resent to ${email}`);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleSendHeldInvitation = async (invitationId: number, email: string) => {
    if (!confirm(`Send the held invitation to ${email}? They'll receive an email and have 7 days to register.`)) {
      return;
    }
    try {
      await userManagementAPI.sendHeldInvitation(cboId, invitationId);
      alert(`Invitation released and sent to ${email}`);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to send invitation');
    }
  };

  // Open the full edit modal for a pending/held invitation
  const handleEditInvitationRole = (invitation: UserInvitation) => {
    setEditingInvitation(invitation);
    setEditInvRole(invitation.role || '');
    setEditInvName(invitation.name || '');
    setEditInvPhone(invitation.phone_number || '');
    setEditInvEmail(invitation.email || '');
    // require_2fa: false = admin exempted; undefined/null/true = required (default)
    setEditInvRequire2FA(invitation.require_2fa !== false);
    setEditInvError(null);
  };

  const handleSaveInvitationRole = async () => {
    if (!editingInvitation) return;
    if (!editInvRole) {
      setEditInvError('Please select a role.');
      return;
    }
    const trimmedEmail = editInvEmail.trim();
    if (!trimmedEmail) {
      setEditInvError('Email cannot be empty.');
      return;
    }

    // Only send fields the admin actually changed.
    const body: {
      role?: string;
      name?: string;
      phone_number?: string;
      email?: string;
      require_2fa?: boolean;
    } = {};
    if (editInvRole !== editingInvitation.role) body.role = editInvRole;
    if (editInvName.trim() !== (editingInvitation.name || '')) body.name = editInvName.trim();
    if (editInvPhone.trim() !== (editingInvitation.phone_number || '')) body.phone_number = editInvPhone.trim();
    if (trimmedEmail.toLowerCase() !== (editingInvitation.email || '').toLowerCase()) body.email = trimmedEmail;
    const currentRequire = editingInvitation.require_2fa !== false; // null/true => required
    if (editInvRequire2FA !== currentRequire) body.require_2fa = editInvRequire2FA;

    if (Object.keys(body).length === 0) {
      setEditingInvitation(null);
      return;
    }

    setEditInvSaving(true);
    setEditInvError(null);
    try {
      await userManagementAPI.updateInvitation(cboId, editingInvitation.id, body);
      setEditingInvitation(null);
      await loadData();
    } catch (err: any) {
      setEditInvError(err.response?.data?.detail || 'Failed to update invitation.');
    } finally {
      setEditInvSaving(false);
    }
  };

  // Portal-member rows carry members.id as `id`, but the account-scoped
  // endpoints (2FA, email change, name/phone PATCH, reset-password, status)
  // key on users.id — an overlapping integer space. Sending the member id
  // 404s ("User not found") at best, and would silently edit an unrelated
  // staff account when the ids collide. The BE list exposes the real users.id
  // as account_user_id; null means the member never accepted a portal account.
  const getAccountUserId = (user: OrganizationUser): number | null =>
    user.user_type === 'member' ? (user.account_user_id ?? null) : user.id;

  // A row worth offering a checkbox for: a real staff account (not a pending
  // invitation row, not a portal member, not a Triveny tenant user) that has
  // never been signed into. "Never logged in" is the only signal the list
  // carries — the server decides for real and tells us who it refused.
  const isSelectableForInvite = (user: OrganizationUser): boolean =>
    !isTriveny &&
    user.user_type !== 'invited' &&
    user.user_type !== 'member' &&
    user.status !== 'inactive' &&
    !user.last_login &&
    !!user.email;

  // Scoped to what is currently ON SCREEN. Select-all while a search is active
  // selecting people the admin cannot see would be the wrong kind of surprise
  // when the next click mails them.
  const selectableUsers = useMemo(
    () => filteredStaffUsers.filter(isSelectableForInvite),
    [filteredStaffUsers, isTriveny],
  );
  const allSelectableSelected =
    selectableUsers.length > 0 && selectableUsers.every((u) => selectedUserIds.has(u.id));

  const toggleUserSelected = (userId: number) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedUserIds((prev) => {
      if (selectableUsers.length > 0 && selectableUsers.every((u) => prev.has(u.id))) return new Set();
      return new Set(selectableUsers.map((u) => u.id));
    });
  };

  // Step 1 of 2. Never sends: asks the server what WOULD happen and opens the
  // preview. Sending is a separate, deliberate click in that modal.
  const handlePreviewBulkInvite = async () => {
    if (!cboId || selectedUserIds.size === 0) return;
    setBulkInviteLoading(true);
    try {
      const preview = await userManagementAPI.bulkInviteUsers(cboId, {
        user_ids: Array.from(selectedUserIds),
        dry_run: true,
      });
      setBulkInvitePreview(preview);
    } catch (err: any) {
      alert(err?.response?.data?.detail || t('admin.bulk_invite_preview_failed', 'Could not prepare the invitations.'));
    } finally {
      setBulkInviteLoading(false);
    }
  };

  // Step 2 of 2 — the one that mails people.
  const handleConfirmBulkInvite = async () => {
    if (!cboId || !bulkInvitePreview) return;
    setBulkInviteSending(true);
    try {
      const result = await userManagementAPI.bulkInviteUsers(cboId, {
        user_ids: Array.from(selectedUserIds),
        dry_run: false,
        send_now: true,
      });
      trackAction('users_bulk_invited', { cbo_id: cboId, created: result.created ?? 0 });
      setBulkInvitePreview(null);
      setSelectedUserIds(new Set());
      await loadData();
      alert(result.message);
    } catch (err: any) {
      alert(err?.response?.data?.detail || t('admin.bulk_invite_failed', 'Could not send the invitations.'));
    } finally {
      setBulkInviteSending(false);
    }
  };

  const handleAdminResetPassword = async (user: OrganizationUser) => {
    const accountId = getAccountUserId(user);
    if (accountId === null) {
      alert(`${user.name} has not activated their portal account yet, so there is no password to reset.`);
      return;
    }
    if (!confirm(`Are you sure you want to reset the password for ${user.name}? They will receive an email with instructions to set a new password.`)) {
      return;
    }

    try {
      const result = await userManagementAPI.adminResetPassword(cboId, accountId);
      alert(result.message || `Password reset initiated for ${user.name}`);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleUpdateUserStatus = async (user: OrganizationUser, newStatus: 'active' | 'inactive') => {
    const action = newStatus === 'inactive' ? 'deactivate' : 'activate';
    const accountId = getAccountUserId(user);
    if (accountId === null) {
      alert(`${user.name} has not activated their portal account yet, so there is no login account to ${action}.`);
      return;
    }
    if (!confirm(`Are you sure you want to ${action} ${user.name}?`)) {
      return;
    }

    try {
      await userManagementAPI.updateUserStatus(cboId, accountId, newStatus);
      alert(`${user.name} has been ${newStatus === 'inactive' ? 'deactivated' : 'activated'} successfully.`);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const openEditUserModal = (user: OrganizationUser) => {
    // user_type='invited' rows carry a synthetic negative id (the BE list
    // helper packs invitation_id as -inv[0] so the merged response is
    // unambiguous). The save flow branches on user_type to route to the
    // invitation PATCH endpoint instead of the users-table one — see
    // handleSaveEditUser.
    setEditingUser(user);
    setEditName(user.name || '');
    setEditPhone(user.phone_number || '');
    setEditEmail(user.email || '');
    setEditEmailEnabled(false);
    setEditEmailReason('');

    // Load the user's current 2FA state so the toggle reflects reality. Only
    // meaningful for accepted users (invited rows have a synthetic negative id
    // and no user_2fa row yet — their 2FA is set via the invite exemption).
    setEditTwoFAInitial(null);
    const isInvitedRow = user.user_type === 'invited' || (typeof user.id === 'number' && user.id < 0);
    const accountUserId = getAccountUserId(user);
    if (!isInvitedRow && accountUserId !== null && cboId && cboId >= 1) {
      (async () => {
        try {
          const res = await fetch(`${getWeaveinApiUrl()}/api/auth/2fa/admin/users/${cboId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
              'X-CBO-ID': String(cboId),
            },
          });
          if (!res.ok) return;
          const data = await res.json();
          const row = (data.users || []).find((u: any) => u.user_id === accountUserId);
          const required = row ? Boolean(row.twofa_required) : true;
          setEditTwoFARequired(required);
          setEditTwoFAInitial(required);
          setEditTwoFAMethod(row?.preferred_method === 'totp' ? 'totp' : 'sms');
          setEditTwoFAPhoneVerified(Boolean(row?.phone_verified));
        } catch {
          // Non-fatal: leave the toggle in its loading state; save will skip 2FA.
        }
      })();
    }
  };

  const handleSaveEditUser = async () => {
    if (!editingUser) return;

    // Guard against a stale CBO context. The app shell sometimes hands us
    // a fallback `cboId=1` (Trust Care Organization) when the active-org
    // selector hasn't hydrated yet, which then 404s every save for users
    // who belong to a different org. Refuse to send the PATCH in that case
    // — the backend error message is opaque to admins ("User not found in
    // this organization") and easily mistaken for a missing endpoint.
    if (!cboId || cboId < 1) {
      alert('Cannot save: the active organization is not loaded yet. Refresh the page and try again.');
      return;
    }

    setEditSaving(true);
    try {
      // Pending-invitation rows route to the invitation PATCH endpoint —
      // they don't have a users-table row yet, so the regular editUserDetails
      // call would 404 against the synthetic negative id. The invitation
      // endpoint accepts name / phone_number / email in one call (the
      // separate /email confirmation flow is unnecessary pre-acceptance).
      const isInvited =
        editingUser.user_type === 'invited' ||
        (typeof editingUser.id === 'number' && editingUser.id < 0);

      if (isInvited) {
        const invitationId = editingUser.invitation_id ?? Math.abs(editingUser.id);
        const body: { name?: string; phone_number?: string; email?: string } = {};
        if (editName !== (editingUser.name || '')) body.name = editName;
        if (editPhone !== (editingUser.phone_number || '')) body.phone_number = editPhone;
        if (editEmailEnabled && editEmail.toLowerCase() !== (editingUser.email || '').toLowerCase()) {
          body.email = editEmail;
        }
        if (Object.keys(body).length === 0) {
          alert('No changes to save.');
          setEditSaving(false);
          return;
        }
        await userManagementAPI.updateInvitation(cboId, invitationId, body);
        alert('Invitation updated. The invitee will see the new details when they accept.');
        setEditingUser(null);
        await loadData();
        return;
      }

      // Everything below edits the users-table account, so it must use the
      // real users.id. For staff rows that's editingUser.id; for portal
      // members it's account_user_id (members.id would hit the wrong record).
      const accountId = getAccountUserId(editingUser);
      if (accountId === null) {
        alert('This member has not activated their portal account yet, so there is no login account to edit.');
        setEditSaving(false);
        return;
      }

      // Handle name/phone update for an already-accepted user
      const data: { name?: string; phone_number?: string } = {};
      if (editName !== (editingUser.name || '')) data.name = editName;
      if (editPhone !== (editingUser.phone_number || '')) data.phone_number = editPhone;

      if (Object.keys(data).length > 0) {
        await userManagementAPI.editUserDetails(cboId, accountId, data);
      }

      // Handle email change (separate endpoint with audit trail)
      if (editEmailEnabled && editEmail.toLowerCase() !== (editingUser.email || '').toLowerCase()) {
        const emailRes = await fetch(`${getWeaveinApiUrl()}/api/cbos/${cboId}/users/${accountId}/email`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'X-CBO-ID': String(cboId),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ new_email: editEmail, reason: editEmailReason || undefined }),
        });
        if (!emailRes.ok) {
          const err = await emailRes.json();
          throw new Error(err.detail || 'Failed to change email');
        }
      }

      // Apply the 2FA toggle if the admin changed it. is_enabled=false is an
      // admin exemption (overrides org enforcement); true re-requires 2FA.
      if (editTwoFAInitial !== null && editTwoFARequired !== editTwoFAInitial) {
        const twoFARes = await fetch(`${getWeaveinApiUrl()}/api/auth/2fa/admin/user/${accountId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'X-CBO-ID': String(cboId),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_enabled: editTwoFARequired, preferred_method: editTwoFAMethod }),
        });
        if (!twoFARes.ok) {
          const err = await twoFARes.json().catch(() => ({}));
          throw new Error(err.detail || 'Failed to update 2FA setting');
        }
      }

      alert('User details updated successfully.');
      setEditingUser(null);
      await loadData();
    } catch (err: any) {
      alert(err.message || err.response?.data?.detail || 'Failed to update user details');
    } finally {
      setEditSaving(false);
    }
  };

  const getRoleBadge = (role: string, displayName?: string | null) => {
    const known = USER_ROLES[role as keyof typeof USER_ROLES];
    // Prefer the org role's display name (e.g. custom roles like "Disaster Case
    // Manager" / role_type dcm01) which aren't in the hardcoded USER_ROLES map.
    // Falls back to the known system-role styling, else a neutral chip.
    const label = displayName || known?.label || role || 'Unknown';
    const color = known?.color || '#6b7280';
    return (
      <span
        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
        style={{ backgroundColor: `${color}20`, color }}
      >
        {label}
      </span>
    );
  };

  const getStatusBadge = (status: string, invitation?: UserInvitation) => {
    const statusConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
      pending: { icon: <Clock className="w-3 h-3" />, color: '#ca8a04', label: 'Pending' },
      held: { icon: <PauseCircle className="w-3 h-3" />, color: '#0891b2', label: 'Held (not sent)' },
      // TRUS-513: a super_admin grant parked for a second super admin. Without
      // its own badge it fell through to "Pending", which reads as "email sent".
      pending_approval: { icon: <Shield className="w-3 h-3" />, color: '#b45309', label: 'Awaiting approval' },
      accepted: { icon: <CheckCircle className="w-3 h-3" />, color: '#16a34a', label: 'Accepted' },
      expired: { icon: <XCircle className="w-3 h-3" />, color: '#dc2626', label: 'Expired' },
      cancelled: { icon: <XCircle className="w-3 h-3" />, color: '#6b7280', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <div className="flex flex-col items-start gap-1">
        <span
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium"
          style={{ backgroundColor: `${config.color}20`, color: config.color }}
        >
          {config.icon}
          <span>{config.label}</span>
        </span>
        {/* BUG-006: when invitation is accepted but the user has never
            actually logged in, surface this as a sub-chip so admins can
            chase the user. Without it, admins see "Accepted" and assume
            onboarding is done — but the invitee may not have realized
            they could sign in (Russell Fairbanks case at AngelFood). */}
        {invitation?.awaiting_first_login && status === 'accepted' && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
            title="Registered, but never logged in. Send a reminder via the resend action."
          >
            <Clock className="w-2.5 h-2.5" />
            <span>Awaiting first login</span>
          </span>
        )}
      </div>
    );
  };

  // Render user table (used for both staff and portal tabs)
  const renderUserTable = (users: OrganizationUser[], isPortal: boolean = false) => {
    // Only the staff list gets a selection column: portal members onboard
    // through member_invitations, and Triveny tenants through a different
    // bridge entirely.
    const showSelection = !isPortal && !isTriveny && canInviteUsers;
    return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <tr>
              {showSelection && (
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    aria-label={t('admin.select_all_never_logged_in', 'Select all users who have never logged in')}
                    title={t('admin.select_all_never_logged_in', 'Select all users who have never logged in')}
                    checked={allSelectableSelected}
                    onChange={toggleSelectAll}
                    disabled={selectableUsers.length === 0}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.name', 'Name')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.email', 'Email')}</th>
              {!isPortal && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.role', 'Role')}</th>}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.phone', 'Phone')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.last_login', 'Last Login')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.compliance', 'Compliance')}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {users.length === 0 ? (
              <tr>
                <td colSpan={(isPortal ? 6 : 7) + (showSelection ? 1 : 0)} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                  {isPortal ? t('admin.no_portal_users_found', 'No portal users found') : t('admin.no_users_found', 'No users found')}
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={`${user.user_type || 'user'}-${user.id}`} className={cn(
                  "hover:bg-gray-50 dark:hover:bg-gray-700",
                  user.status === 'inactive' && "bg-gray-50 dark:bg-gray-900/40 opacity-60"
                )}>
                  {showSelection && (
                    <td className="px-4 py-4">
                      {isSelectableForInvite(user) ? (
                        <input
                          type="checkbox"
                          aria-label={t('admin.select_user_for_invite', 'Select {{name}} for invitation', { name: user.name })}
                          checked={selectedUserIds.has(user.id)}
                          onChange={() => toggleUserSelected(user.id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      ) : (
                        // Rendered as a blank cell, not a disabled box: a
                        // checkbox you cannot tick invites the question "why
                        // not?" on every row that has already been claimed.
                        <span className="block w-4" />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                      {user.name}
                      {user.is_primary && (
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs font-semibold uppercase">
                          {t('admin.primary', 'Primary')}
                        </span>
                      )}
                      {user.status === 'inactive' && (
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs font-semibold uppercase">
                          {t('admin.deactivated', 'Deactivated')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">{user.email}</td>
                  {!isPortal && <td className="px-4 py-4">{getRoleBadge(user.user_org_role, user.role_names)}</td>}
                  <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">{user.phone_number || '—'}</td>
                  <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                    {user.last_login
                      ? fmt.short(user.last_login)
                      : 'Never'}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col gap-1">
                      {/* Terms of Service */}
                      <div className="flex items-center gap-1">
                        {user.terms_accepted ? (
                          <span
                            className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded text-xs font-semibold uppercase cursor-help"
                            title={`${t('admin.terms_accepted', 'Terms Accepted')}${user.terms_signed_version ? `\nVersion: ${user.terms_signed_version}` : ''}${user.terms_signed_at ? `\nSigned: ${fmt.short(user.terms_signed_at)}` : ''}`}
                          >
                            {t('admin.tos', 'ToS')} {user.terms_signed_version && <span className="font-normal">v{user.terms_signed_version}</span>} ✓
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs font-semibold uppercase" title={t('admin.terms_not_accepted', 'Terms Not Accepted')}>
                            {t('admin.tos', 'ToS')}
                          </span>
                        )}
                        {user.terms_signed_at && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {fmt.short(user.terms_signed_at)}
                          </span>
                        )}
                      </div>
                      {/* Triveny: Privacy + AI. WeaveIn: HIPAA. */}
                      {isTriveny ? (
                        <>
                          {/* Privacy */}
                          <div className="flex items-center gap-1">
                            {user.privacy_accepted ? (
                              <span
                                className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded text-xs font-semibold uppercase cursor-help"
                                title={`${t('admin.privacy_accepted', 'Privacy Policy Accepted')}${user.privacy_signed_version ? `\nVersion: ${user.privacy_signed_version}` : ''}${user.privacy_signed_at ? `\nSigned: ${fmt.short(user.privacy_signed_at)}` : ''}`}
                              >
                                {t('admin.privacy', 'Privacy')} {user.privacy_signed_version && <span className="font-normal">v{user.privacy_signed_version}</span>} ✓
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs font-semibold uppercase" title={t('admin.privacy_not_accepted', 'Privacy Policy Not Accepted')}>
                                {t('admin.privacy', 'Privacy')}
                              </span>
                            )}
                            {user.privacy_signed_at && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {fmt.short(user.privacy_signed_at)}
                              </span>
                            )}
                          </div>
                          {/* AI Disclosure */}
                          <div className="flex items-center gap-1">
                            {user.ai_accepted ? (
                              <span
                                className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded text-xs font-semibold uppercase cursor-help"
                                title={`${t('admin.ai_accepted', 'AI Disclosure Accepted')}${user.ai_signed_version ? `\nVersion: ${user.ai_signed_version}` : ''}${user.ai_signed_at ? `\nSigned: ${fmt.short(user.ai_signed_at)}` : ''}`}
                              >
                                {t('admin.ai', 'AI')} {user.ai_signed_version && <span className="font-normal">v{user.ai_signed_version}</span>} ✓
                              </span>
                            ) : (
                              <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs font-semibold uppercase" title={t('admin.ai_not_accepted', 'AI Disclosure Not Accepted')}>
                                {t('admin.ai', 'AI')}
                              </span>
                            )}
                            {user.ai_signed_at && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {fmt.short(user.ai_signed_at)}
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-1">
                          {user.hipaa_accepted ? (
                            <span
                              className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded text-xs font-semibold uppercase cursor-help"
                              title={`${t('admin.hipaa_accepted', 'HIPAA Accepted')}${user.hipaa_signed_version ? `\nVersion: ${user.hipaa_signed_version}` : ''}${user.hipaa_signed_at ? `\nSigned: ${fmt.short(user.hipaa_signed_at)}` : ''}`}
                            >
                              HIPAA {user.hipaa_signed_version && <span className="font-normal">v{user.hipaa_signed_version}</span>} ✓
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs font-semibold uppercase" title={t('admin.hipaa_not_accepted', 'HIPAA Not Accepted')}>
                              HIPAA
                            </span>
                          )}
                          {user.hipaa_signed_at && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {fmt.short(user.hipaa_signed_at)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex gap-2 items-center">
                      {canManageAssignments && !isPortal && (
                        <button
                          onClick={() => {
                            if (isTriveny) {
                              if (user.tenant_user_id) setSelectedTrivenyUserForRoles(user);
                            } else {
                              setSelectedUserForRoles(user);
                            }
                          }}
                          disabled={isTriveny && !user.tenant_user_id}
                          title={t('admin.manage_organization_roles', 'Manage organization roles')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
                        >
                          <Shield className="w-4 h-4" />
                          <span>{t('admin.roles', 'Roles')}</span>
                        </button>
                      )}
                      {canManageAssignments && !isPortal && !isTriveny && onNavigate && (
                        <button
                          onClick={() => onNavigate('user-permissions', { userId: user.id, userName: user.name })}
                          title={t('admin.view_effective_permissions', 'View effective permissions')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          <span>{t('admin.permissions', 'Permissions')}</span>
                        </button>
                      )}
                      {canManageAssignments && !isPortal && !isTriveny && (
                        <button
                          onClick={() => setSelectedUserForAssignments(user)}
                          title={t('admin.manage_datalevel_permissions', 'Manage data-level permissions')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          <span>{t('admin.assignments', 'Assignments')}</span>
                        </button>
                      )}
                      {canViewAsUsers &&
                        user.user_type !== 'invited' &&
                        user.status !== 'inactive' &&
                        getAccountUserId(user) !== null &&
                        !isInternalSystemRole(user.user_org_role) && (
                          <button
                            onClick={() =>
                              setViewAsTarget({
                                id: getAccountUserId(user) as number,
                                name: user.name,
                                email: user.email,
                                kind: isPortal ? 'member' : 'staff',
                                role: isPortal ? 'member' : user.role_names || user.user_org_role,
                              })
                            }
                            title={t('admin.view_as_user', 'Open the app as this person (super admin, recorded)')}
                            className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 text-white text-xs font-medium rounded-md hover:bg-gray-900 dark:bg-gray-600 dark:hover:bg-gray-500 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            <span>{t('admin.view_as', 'View as')}</span>
                          </button>
                        )}
                      {!isPortal && !isTriveny && user.user_type !== 'invited' && (
                        <button
                          onClick={() => setSelectedUserForActivity(user)}
                          title={t('admin.view_user_activity', 'Login history & page/API activity')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-md hover:bg-amber-700 transition-colors"
                        >
                          <Activity className="w-4 h-4" />
                          <span>{t('admin.activity', 'Activity')}</span>
                        </button>
                      )}
                      {canEditUserDetails && (
                        <button
                          onClick={() => openEditUserModal(user)}
                          title={
                            user.user_type === 'invited'
                              ? t('admin.edit_pending_invitation', 'Edit pending invitation')
                              : t('admin.edit_user_details', 'Edit user details')
                          }
                          className="flex items-center gap-1 px-2 py-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-xs font-medium"
                        >
                          <Pencil className="w-4 h-4" />
                          <span>{t('admin.edit', 'Edit')}</span>
                        </button>
                      )}
                      {canDeleteUsers && (
                        <>
                          <button
                            onClick={() => handleAdminResetPassword(user)}
                            title={t('admin.reset_password', 'Reset password')}
                            className="flex items-center gap-1 px-2 py-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded transition-colors text-xs font-medium"
                          >
                            <KeyRound className="w-4 h-4" />
                            <span>{t('admin.reset_pw', 'Reset PW')}</span>
                          </button>
                          <button
                            onClick={() => handleUpdateUserStatus(user, user.status === 'inactive' ? 'active' : 'inactive')}
                            title={user.status === 'inactive' ? t('admin.activate_user', 'Activate user') : t('admin.deactivate_user', 'Deactivate user')}
                            className={cn(
                              "flex items-center gap-1 px-2 py-1.5 rounded transition-colors text-xs font-medium",
                              user.status === 'inactive'
                                ? "text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30"
                                : "text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/30"
                            )}
                          >
                            <Power className="w-4 h-4" />
                            <span>{user.status === 'inactive' ? t('admin.activate', 'Activate') : t('admin.deactivate', 'Deactivate')}</span>
                          </button>
                          <button
                            onClick={() => handleRemoveUser(user.id, user.name, user.user_type || 'user')}
                            title={t('admin.remove_from_organization', 'Remove from organization')}
                            className="flex items-center gap-1 px-2 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors text-xs font-medium"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>{t('admin.remove', 'Remove')}</span>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
        {users.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
            {isPortal ? t('admin.no_portal_users_found', 'No portal users found') : t('admin.no_users_found', 'No users found')}
          </div>
        ) : (
          users.map((user) => (
            <div key={`${user.user_type || 'user'}-${user.id}`} className={cn(
              "p-4 space-y-3",
              user.status === 'inactive' && "bg-gray-50 dark:bg-gray-900/40 opacity-60"
            )}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  {showSelection && isSelectableForInvite(user) && (
                    <input
                      type="checkbox"
                      aria-label={t('admin.select_user_for_invite', 'Select {{name}} for invitation', { name: user.name })}
                      checked={selectedUserIds.has(user.id)}
                      onChange={() => toggleUserSelected(user.id)}
                      className="w-4 h-4 mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  )}
                  <div>
                  <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                    {user.name}
                    {user.is_primary && (
                      <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded text-xs font-semibold uppercase">
                        {t('admin.primary_1', 'Primary')}
                      </span>
                    )}
                    {user.status === 'inactive' && (
                      <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs font-semibold uppercase">
                        {t('admin.deactivated', 'Deactivated')}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{user.email}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                {!isPortal && (
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 block text-xs">{t('admin.role_1', 'Role')}</span>
                    <div className="mt-1">{getRoleBadge(user.user_org_role, user.role_names)}</div>
                  </div>
                )}
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block text-xs">{t('admin.phone_1', 'Phone')}</span>
                  <span className="text-gray-900 dark:text-white">{user.phone_number || '—'}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block text-xs">{t('admin.last_login_1', 'Last Login')}</span>
                  <span className="text-gray-900 dark:text-white">
                    {user.last_login ? fmt.short(user.last_login) : 'Never'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block text-xs">{t('admin.compliance_1', 'Compliance')}</span>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {user.terms_accepted ? (
                      <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded text-xs font-semibold uppercase">{t('admin.tos_1', 'ToS')} ✓</span>
                    ) : (
                      <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs font-semibold uppercase">{t('admin.tos_1', 'ToS')}</span>
                    )}
                    {isTriveny ? (
                      <>
                        {user.privacy_accepted ? (
                          <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded text-xs font-semibold uppercase">{t('admin.privacy', 'Privacy')} ✓</span>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs font-semibold uppercase">{t('admin.privacy', 'Privacy')}</span>
                        )}
                        {user.ai_accepted ? (
                          <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded text-xs font-semibold uppercase">{t('admin.ai', 'AI')} ✓</span>
                        ) : (
                          <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs font-semibold uppercase">{t('admin.ai', 'AI')}</span>
                        )}
                      </>
                    ) : user.hipaa_accepted ? (
                      <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded text-xs font-semibold uppercase">HIPAA ✓</span>
                    ) : (
                      <span className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs font-semibold uppercase">HIPAA</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-2">
                {canManageAssignments && !isPortal && (
                  <button
                    onClick={() => {
                      if (isTriveny) {
                        if (user.tenant_user_id) setSelectedTrivenyUserForRoles(user);
                      } else {
                        setSelectedUserForRoles(user);
                      }
                    }}
                    disabled={isTriveny && !user.tenant_user_id}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
                  >
                    <Shield className="w-4 h-4" />
                    <span>{t('admin.manage_roles', 'Manage Roles')}</span>
                  </button>
                )}
                {canManageAssignments && !isPortal && !isTriveny && (
                  <button
                    onClick={() => setSelectedUserForAssignments(user)}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    <span>{t('admin.manage_assignments', 'Manage Assignments')}</span>
                  </button>
                )}
                {canEditUserDetails && (
                  <button
                    onClick={() => openEditUserModal(user)}
                    className="flex items-center justify-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    <span>
                      {user.user_type === 'invited'
                        ? t('admin.edit_invitation', 'Edit Invitation')
                        : t('admin.edit_details', 'Edit Details')}
                    </span>
                  </button>
                )}
                {canDeleteUsers && (
                  <>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAdminResetPassword(user)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-700 text-sm font-medium rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors"
                      >
                        <KeyRound className="w-4 h-4" />
                        <span>{t('admin.reset_password', 'Reset Password')}</span>
                      </button>
                      <button
                        onClick={() => handleUpdateUserStatus(user, user.status === 'inactive' ? 'active' : 'inactive')}
                        className={cn(
                          "flex-1 flex items-center justify-center gap-2 px-3 py-2 border text-sm font-medium rounded-md transition-colors",
                          user.status === 'inactive'
                            ? "text-green-600 dark:text-green-400 border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/30"
                            : "text-orange-600 dark:text-orange-400 border-orange-300 dark:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/30"
                        )}
                      >
                        <Power className="w-4 h-4" />
                        <span>{user.status === 'inactive' ? t('admin.activate', 'Activate') : t('admin.deactivate', 'Deactivate')}</span>
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemoveUser(user.id, user.name, user.user_type || 'user')}
                      className="flex items-center justify-center gap-2 px-3 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 text-sm font-medium rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>{isPortal ? t('admin.remove_member', 'Remove Member') : t('admin.remove_user', 'Remove User')}</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('admin.loading_users', 'Loading users...')}</div>
      </div>
    );
  }

  // Show pending AND held — held invitations are staged but not yet emailed
  // and need admin action ("Send invite now") to be released. TRUS-513 adds
  // pending_approval: a super_admin grant waiting on a second super admin. All
  // three are open invitations someone still has to act on, so all three count.
  const pendingInvitations = invitations.filter(
    i => i.status === 'pending' || i.status === 'held' || i.status === 'pending_approval',
  );
  const userInvitations = filteredInvitations.filter(i => i.invitation_type !== 'member');
  const memberInvitations = filteredInvitations.filter(i => i.invitation_type === 'member');

  return (
    // Triveny.ai: .triveny-platform activates the design-system overlay
    // (paper bg, brand typography, tokens, Tv* primitives). On WeaveIn the
    // class is omitted and the page renders with its legacy Tailwind look.
    <div className={`p-4 md:p-6 lg:p-8 max-w-7xl mx-auto${productConfig.isTriveny ? ' triveny-platform' : ''}`}>
      {/* Toolbar (page title rendered by the shell chrome) */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <p className="text-[13px] text-ink-500">{organizationName}</p>

        {canManageAssignments && !isTriveny && (
          <button
            onClick={() => setShowBulkReassign(true)}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
          >
            <ArrowRight className="w-4 h-4" />
            <span>Reassign Caseload</span>
          </button>
        )}
        {!isTriveny && (
          <button
            onClick={() => setShowOrgActivity(true)}
            title={t('admin.org_activity', 'Org-wide login, API & page activity')}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
          >
            <Activity className="w-4 h-4" />
            <span>{t('admin.org_activity_btn', 'Org Activity')}</span>
          </button>
        )}
        {canInviteUsers && !isTriveny && selectedUserIds.size > 0 && activeTab === 'users' && (
          <button
            onClick={handlePreviewBulkInvite}
            disabled={bulkInviteLoading || orgUnresolved}
            title={t('admin.invite_selected_title', 'Preview and send invitations to the selected users')}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {bulkInviteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            <span>
              {t('admin.invite_selected', 'Invite Selected')} ({selectedUserIds.size})
            </span>
          </button>
        )}
        {canInviteUsers && (
          <button
            onClick={() => setShowInviteModal(true)}
            disabled={orgUnresolved}
            title={
              orgUnresolved
                ? t(
                    'admin.invite_blocked_org_unresolved',
                    'Still resolving which organization you are in — inviting now could add the user to the wrong one.',
                  )
                : undefined
            }
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
          >
            <UserPlus className="w-4 h-4" />
            <span>{t('admin.invite_user', 'Invite User')}</span>
          </button>
        )}
      </div>

      {/* TRUS-530: say why the invite button is dead, rather than leaving a
          greyed-out control with no explanation. */}
      {orgUnresolved && canInviteUsers && (
        <div className="flex items-start gap-2 px-4 py-3 mb-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-300">
          <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <span>
              {t(
                'admin.org_unresolved_notice',
                'Still resolving which organization you are in. Inviting is paused until then, so nobody is added to the wrong organization.',
              )}
            </span>
            <button
              onClick={() => window.location.reload()}
              className="ml-2 underline font-medium hover:no-underline"
            >
              {t('admin.reload', 'Reload')}
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
          <XCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <button
          onClick={() => setActiveTab('users')}
          className={cn(
            "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
            activeTab === 'users'
              ? "border-blue-600 text-blue-600 dark:text-blue-400"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
          )}
        >
          <Users className="w-4 h-4" />
          <span>Staff Users ({staffUsers.length})</span>
        </button>
        <button
          onClick={() => setActiveTab('portal')}
          className={cn(
            "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
            activeTab === 'portal'
              ? "border-cyan-600 text-cyan-600 dark:text-cyan-400"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400"
          )}
        >
          <UserCircle className="w-4 h-4" />
          <span>Portal Users ({portalUsers.length})</span>
        </button>
        {canInviteUsers && (
          <button
            onClick={() => setActiveTab('invitations')}
            className={cn(
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
              activeTab === 'invitations'
                ? "border-amber-600 text-amber-600 dark:text-amber-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400"
            )}
          >
            <Mail className="w-4 h-4" />
            <span>Invitations ({pendingInvitations.length})</span>
          </button>
        )}
        {currentUserRole === 'super_admin' && !isTriveny && (
          <button
            onClick={() => setActiveTab('platform-admins')}
            className={cn(
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
              activeTab === 'platform-admins'
                ? "border-purple-600 text-purple-600 dark:text-purple-400"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400"
            )}
          >
            <Shield className="w-4 h-4" />
            <span>Platform Admins{platformAdmins.length ? ` (${platformAdmins.length})` : ''}</span>
          </button>
        )}
        {canViewAsUsers && (
          <button
            onClick={() => setActiveTab('view-as-log')}
            className={cn(
              "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
              activeTab === 'view-as-log'
                ? "border-gray-800 text-gray-900 dark:border-gray-300 dark:text-white"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            )}
          >
            <Eye className="w-4 h-4" />
            <span>{t('admin.view_as_log', 'View as log')}</span>
          </button>
        )}
      </div>

      {/* TRUS-747: View as user sessions in this org */}
      {activeTab === 'view-as-log' && canViewAsUsers && <ImpersonationLog cboId={cboId} />}

      {/* Staff Users Tab */}
      {activeTab === 'users' && (
        <>
          <SearchInput
            value={userSearch}
            onChange={setUserSearch}
            placeholder={t('admin.search_users', 'Search by name, email, or role...')}
          />
          {renderUserTable(filteredStaffUsers, false)}
        </>
      )}

      {/* Portal Users Tab */}
      {activeTab === 'portal' && (
        <>
          <SearchInput
            value={portalSearch}
            onChange={setPortalSearch}
            placeholder={t('admin.search_portal_users', 'Search by name or email...')}
          />
          {renderUserTable(filteredPortalUsers, true)}
        </>
      )}

      {/* Platform Admins Tab — super_admin only; global roster + cross-org activity drill-down */}
      {activeTab === 'platform-admins' && currentUserRole === 'super_admin' && (
        <div>
          {/* TRUS-513: the approval queue sits above the roster because a queued
              request is the actionable thing — the roster is reference. */}
          <SuperAdminApprovals cboId={cboId} onChanged={loadData} />
          <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3">
            Every platform admin (super_admin / ops_admin), independent of organization. Click <strong>Activity</strong> on a
            row to audit that admin&apos;s usage across all organizations.
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {['Admin', 'Role', 'Last active', 'Last login', 'Logins (90d)', 'API calls', 'Page events', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loadingPlatformAdmins ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
                ) : platformAdmins.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No platform admins.</td></tr>
                ) : (
                  platformAdmins.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{a.name || a.email || 'Unknown'}</div>
                        {a.email && a.name && <div className="text-xs text-gray-500 dark:text-gray-400">{a.email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full', a.role === 'super_admin'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                          : 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300')}>{a.role}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{a.last_active ? fmt.short(a.last_active) : '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{a.last_login ? fmt.short(a.last_login) : '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{a.logins ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{a.api_calls ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{a.page_events ?? 0}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedUserForActivity({ id: a.id, name: a.name, email: a.email } as OrganizationUser)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-sm text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded transition-colors"
                        >
                          <Activity className="w-4 h-4" /><span>Activity</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <>
          <SearchInput
            value={invitationSearch}
            onChange={setInvitationSearch}
            placeholder={t('admin.search_invitations', 'Search by name, email, role, or status...')}
          />

          {/* Staff Invitations Section */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Staff Invitations ({userInvitations.length})
            </h3>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.name_1', 'Name')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.email_1', 'Email')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.role_2', 'Role')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.invited_by', 'Invited By')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.status', 'Status')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.expires', 'Expires')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.actions_1', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {userInvitations.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          {t('admin.no_staff_invitations', 'No staff invitations')}
                        </td>
                      </tr>
                    ) : (
                      userInvitations.map((invitation) => (
                        <tr key={`user-${invitation.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-4 font-medium text-gray-900 dark:text-white">{invitation.name}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">{invitation.email}</td>
                          <td className="px-4 py-4">{getRoleBadge(invitation.role, invitation.role_names)}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">{invitation.invited_by_name}</td>
                          <td className="px-4 py-4">{getStatusBadge(invitation.status, invitation)}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                            <div>{fmt.short(invitation.expires_at)}</div>
                            <div className={`text-xs ${new Date(invitation.expires_at) < new Date() ? 'text-red-500' : 'text-gray-400'}`}>
                              {fmt.relative(invitation.expires_at)}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {['pending', 'expired', 'held'].includes(invitation.status) && canManageInvitations && (
                              <div className="flex items-center gap-1">
                                {invitation.status === 'held' && (
                                  <button
                                    onClick={() => handleSendHeldInvitation(invitation.id, invitation.email)}
                                    title="Send invitation now (release from hold)"
                                    className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded transition-colors"
                                  >
                                    <Send className="w-4 h-4" />
                                  </button>
                                )}
                                {invitation.status !== 'held' && (
                                  <button
                                    onClick={() => handleResendInvitation(invitation.id, invitation.email, 'user')}
                                    title={invitation.status === 'expired' ? t('admin.reactivate_invitation', 'Reactivate & resend invitation') : t('admin.resend_invitation', 'Resend invitation')}
                                    className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                                  >
                                    <RefreshCw className="w-4 h-4" />
                                  </button>
                                )}
                                {(invitation.status === 'pending' || invitation.status === 'held') && (
                                  <button
                                    onClick={() => handleEditInvitationRole(invitation)}
                                    title="Edit role"
                                    className="p-1.5 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded transition-colors"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                )}
                                {(invitation.status === 'pending' || invitation.status === 'held') && (
                                <button
                                  onClick={() => handleCancelInvitation(invitation.id, invitation.email, 'user')}
                                  title={t('admin.cancel_invitation_1', 'Cancel invitation')}
                                  className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {userInvitations.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('admin.no_staff_invitations', 'No staff invitations')}
                  </div>
                ) : (
                  userInvitations.map((invitation) => (
                    <div key={`user-${invitation.id}`} className="p-4 space-y-3">
                      <div>
                        <span className="font-medium text-gray-900 dark:text-white">{invitation.name}</span>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{invitation.email}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 block text-xs">{t('admin.role_3', 'Role')}</span>
                          <div className="mt-1">{getRoleBadge(invitation.role, invitation.role_names)}</div>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 block text-xs">{t('admin.status_1', 'Status')}</span>
                          <div className="mt-1">{getStatusBadge(invitation.status, invitation)}</div>
                        </div>
                      </div>
                      {['pending', 'expired', 'held'].includes(invitation.status) && canManageInvitations && (
                        <div className="flex gap-2">
                          {invitation.status === 'held' ? (
                            <button
                              onClick={() => handleSendHeldInvitation(invitation.id, invitation.email)}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700 text-sm font-medium rounded-md hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                            >
                              <Send className="w-4 h-4" />
                              <span>Send invite</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleResendInvitation(invitation.id, invitation.email, 'user')}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 text-sm font-medium rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                            >
                              <RefreshCw className="w-4 h-4" />
                              <span>{invitation.status === 'expired' ? t('admin.reactivate', 'Reactivate') : t('admin.resend', 'Resend')}</span>
                            </button>
                          )}
                          {(invitation.status === 'pending' || invitation.status === 'held') && (
                            <button
                              onClick={() => handleEditInvitationRole(invitation)}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-700 text-sm font-medium rounded-md hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors"
                            >
                              <Pencil className="w-4 h-4" />
                              <span>Edit role</span>
                            </button>
                          )}
                          {(invitation.status === 'pending' || invitation.status === 'held') && (
                          <button
                            onClick={() => handleCancelInvitation(invitation.id, invitation.email, 'user')}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 text-sm font-medium rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                            <span>{t('admin.cancel', 'Cancel')}</span>
                          </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Portal Invitations Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <UserCircle className="w-4 h-4 text-cyan-600" />
              Portal Invitations ({memberInvitations.length})
            </h3>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.name_1', 'Name')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.email_1', 'Email')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.invited_by', 'Invited By')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.status', 'Status')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.expires', 'Expires')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('admin.actions_1', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {memberInvitations.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                          {t('admin.no_portal_invitations', 'No portal invitations')}
                        </td>
                      </tr>
                    ) : (
                      memberInvitations.map((invitation) => (
                        <tr key={`member-${invitation.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 dark:text-white">{invitation.name}</span>
                              <span className="px-1.5 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded text-xs font-semibold uppercase">
                                Portal
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">{invitation.email}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">{invitation.invited_by_name}</td>
                          <td className="px-4 py-4">{getStatusBadge(invitation.status, invitation)}</td>
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-white">
                            <div>{fmt.short(invitation.expires_at)}</div>
                            <div className={`text-xs ${new Date(invitation.expires_at) < new Date() ? 'text-red-500' : 'text-gray-400'}`}>
                              {fmt.relative(invitation.expires_at)}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {['pending', 'expired'].includes(invitation.status) && canDeleteUsers && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleResendInvitation(invitation.id, invitation.email, 'member')}
                                  title={invitation.status === 'expired' ? t('admin.reactivate_invitation', 'Reactivate & resend invitation') : t('admin.resend_invitation', 'Resend invitation')}
                                  className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                </button>
                                {invitation.status === 'pending' && (
                                <button
                                  onClick={() => handleCancelInvitation(invitation.id, invitation.email, 'member')}
                                  title={t('admin.cancel_invitation_1', 'Cancel invitation')}
                                  className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {memberInvitations.length === 0 ? (
                  <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {t('admin.no_portal_invitations', 'No portal invitations')}
                  </div>
                ) : (
                  memberInvitations.map((invitation) => (
                    <div key={`member-${invitation.id}`} className="p-4 space-y-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">{invitation.name}</span>
                          <span className="px-1.5 py-0.5 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 rounded text-xs font-semibold uppercase">
                            Portal
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{invitation.email}</div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 block text-xs">{t('admin.invited_by_1', 'Invited By')}</span>
                          <span className="text-gray-900 dark:text-white">{invitation.invited_by_name}</span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400 block text-xs">{t('admin.status_1', 'Status')}</span>
                          <div className="mt-1">{getStatusBadge(invitation.status, invitation)}</div>
                        </div>
                      </div>
                      {['pending', 'expired'].includes(invitation.status) && canDeleteUsers && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleResendInvitation(invitation.id, invitation.email, 'member')}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 text-sm font-medium rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                          >
                            <RefreshCw className="w-4 h-4" />
                            <span>{invitation.status === 'expired' ? t('admin.reactivate', 'Reactivate') : t('admin.resend', 'Resend')}</span>
                          </button>
                          {invitation.status === 'pending' && (
                          <button
                            onClick={() => handleCancelInvitation(invitation.id, invitation.email, 'member')}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 text-sm font-medium rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                            <span>{t('admin.cancel', 'Cancel')}</span>
                          </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* User Setup Wizard (replaces InviteUserModal) */}
      {showInviteModal && !orgUnresolved && (
        <UserSetupWizard
          cboId={cboId}
          resolvedOrgId={resolvedOrgId}
          organizationName={organizationName}
          currentUserRole={currentUserRole}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            trackAction('user_invited', { cbo_id: cboId });
            loadData();
          }}
        />
      )}

      {/* Manage User Roles Modal */}
      {selectedUserForRoles && (
        <ManageUserRolesModal
          userId={selectedUserForRoles.id}
          userName={selectedUserForRoles.name}
          cboId={cboId}
          onClose={() => setSelectedUserForRoles(null)}
          onRolesChanged={() => loadData()}
        />
      )}

      {/* Triveny role-change modal (talks to /api/platform/roles) */}
      {selectedTrivenyUserForRoles && selectedTrivenyUserForRoles.tenant_user_id && (
        <TrivenyRoleChangeModal
          tenantUserId={selectedTrivenyUserForRoles.tenant_user_id}
          userName={selectedTrivenyUserForRoles.name}
          userEmail={selectedTrivenyUserForRoles.email}
          onClose={() => setSelectedTrivenyUserForRoles(null)}
          onSaved={() => loadData()}
        />
      )}

      {/* User Assignments Modal */}
      {selectedUserForAssignments && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedUserForAssignments(null)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <UserAssignments
              userId={selectedUserForAssignments.id}
              userName={selectedUserForAssignments.name}
              userRole={selectedUserForAssignments.user_org_role || 'member'}
              organizationId={cboId}
              onClose={() => setSelectedUserForAssignments(null)}
            />
          </div>
        </div>
      )}

      {/* Per-user activity: login history + page/API access */}
      <ViewAsUserModal
        open={!!viewAsTarget}
        onClose={() => setViewAsTarget(null)}
        cboId={cboId}
        target={viewAsTarget}
      />

      {selectedUserForActivity && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedUserForActivity(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-5xl max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Activity className="w-4 h-4 text-amber-600" />
                Activity — {selectedUserForActivity.name || selectedUserForActivity.email}
              </h3>
              <button
                onClick={() => setSelectedUserForActivity(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <AccessActivityReport userId={selectedUserForActivity.id} />
            </div>
          </div>
        </div>
      )}

      {/* Account-wide removal confirmation (super_admin/ops_admin targets) */}
      {accountWideRemoval && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => !accountWideRemoving && setAccountWideRemoval(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <Trash2 className="w-5 h-5 text-red-600" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Remove from ALL organizations?
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {accountWideRemoval.message}
              </p>
              {accountWideRemoval.affectedOrganizations.length > 0 && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                    Access that will be revoked ({accountWideRemoval.affectedOrganizations.length})
                  </p>
                  <ul className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                    {accountWideRemoval.affectedOrganizations.map((org) => (
                      <li key={org.cbo_id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-gray-800 dark:text-gray-200">{org.name || `Org #${org.cbo_id}`}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{org.role}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setAccountWideRemoval(null)}
                disabled={accountWideRemoving}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmAccountWideRemoval}
                disabled={accountWideRemoving}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {accountWideRemoving ? 'Removing…' : 'Remove from all'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Org-level activity: all users in this org, filterable by user */}
      {showOrgActivity && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowOrgActivity(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-5xl max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Activity className="w-4 h-4 text-amber-600" />
                Org Activity — {organizationName}
              </h3>
              <button
                onClick={() => setShowOrgActivity(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                aria-label="Close"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <AccessActivityReport cboId={cboId} />
            </div>
          </div>
        </div>
      )}

      {/* Bulk invite preview. This modal IS the safety mechanism: the button
          that opened it only ran a dry run, and the request that actually
          mails people is the one behind "Send". It shows three things the
          admin cannot get anywhere else — who is really invitable, who was
          refused and why, and who will land in an empty member list. */}
      {bulkInvitePreview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => !bulkInviteSending && setBulkInvitePreview(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('admin.bulk_invite_review', 'Review invitations')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('admin.bulk_invite_nothing_sent_yet', 'Nothing has been sent yet.')}{' '}
                {bulkInvitePreview.organization_name}
              </p>
            </div>

            <div className="px-6 py-4 overflow-y-auto space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                <Mail className="w-4 h-4 text-teal-600" />
                {t('admin.bulk_invite_will_receive', '{{count}} person(s) will receive an invitation', {
                  count: bulkInvitePreview.would_invite ?? 0,
                })}
              </div>

              {bulkInvitePreview.invitations.length > 0 && (
                <ul className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  {bulkInvitePreview.invitations.map((inv) => (
                    <li key={inv.user_id} className="px-3 py-2 flex items-center justify-between gap-3">
                      <span className="text-gray-900 dark:text-white truncate">
                        {inv.name} <span className="text-gray-500 dark:text-gray-400">· {inv.email}</span>
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">{inv.role}</span>
                        {inv.has_member_visibility === false && (
                          <span
                            title={t('admin.no_member_visibility_hint', 'This role reaches no program, so their member list will be empty.')}
                            className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-xs font-semibold uppercase"
                          >
                            {t('admin.no_members', 'No members')}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {bulkInvitePreview.warnings.map((w) => (
                <div
                  key={w.code}
                  className="flex gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg"
                >
                  <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 dark:text-amber-300">{w.message}</p>
                </div>
              ))}

              {bulkInvitePreview.skipped_detail.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    {t('admin.bulk_invite_skipped', 'Not invited ({{count}})', {
                      count: bulkInvitePreview.skipped_detail.length,
                    })}
                  </p>
                  <ul className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                    {bulkInvitePreview.skipped_detail.map((sk) => (
                      <li key={sk.user_id} className="px-3 py-2 flex items-center justify-between gap-3">
                        <span className="text-gray-500 dark:text-gray-400 truncate">{sk.email || sk.user_id}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                          {SKIP_REASON_KEYS[sk.reason] ? t(SKIP_REASON_KEYS[sk.reason][0], SKIP_REASON_KEYS[sk.reason][1]) : sk.reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setBulkInvitePreview(null)}
                disabled={bulkInviteSending}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleConfirmBulkInvite}
                disabled={bulkInviteSending || (bulkInvitePreview.would_invite ?? 0) === 0}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {bulkInviteSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {bulkInviteSending
                  ? t('admin.sending', 'Sending...')
                  : t('admin.send_invitations', 'Send {{count}} invitation(s)', {
                      count: bulkInvitePreview.would_invite ?? 0,
                    })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reassign Caseload Modal */}
      {showBulkReassign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkReassign(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <BulkReassignDialog
              cboId={cboId}
              onClose={() => setShowBulkReassign(false)}
              onComplete={() => loadData()}
            />
          </div>
        </div>
      )}

      {/* Edit User Details Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingUser.user_type === 'invited'
                  ? t('admin.edit_pending_invitation', 'Edit Pending Invitation')
                  : t('admin.edit_user', 'Edit User')}
              </h3>
              <button
                onClick={() => setEditingUser(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              {editingUser.user_type === 'invited' && (
                <div className="text-xs bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-100 rounded-lg px-3 py-2">
                  This invitee hasn&apos;t accepted yet. Edits apply to the
                  pending invitation; the invitee will see the new details
                  when they accept.
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.name', 'Name')}
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.twofa_login_phone', '2FA / Login Phone')}
                </label>
                {/* TRUS-251: This is the user's SMS 2FA / login number. An admin must NOT be
                    able to set or change another user's 2FA phone — only the user can, by
                    receiving and verifying a code. So the field is read-only here; recovery for
                    a lost number is the "Require 2FA at login" toggle below. */}
                <input
                  type="text"
                  value={editPhone}
                  readOnly
                  aria-readonly="true"
                  tabIndex={-1}
                  placeholder={t('admin.no_phone_on_file', 'No phone on file')}
                  className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed focus:outline-none"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t(
                    'admin.twofa_phone_help',
                    'Used for SMS login codes. For security, this number can only be added or changed by the user themselves (they must receive and verify a code). To help a user who has lost access to this number, uncheck “Require 2FA at login for this user” below so they can sign in and re-enroll a new number.'
                  )}
                </p>
              </div>

              {/* Email Change Section */}
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('admin.email', 'Email')}
                  </label>
                  {!editEmailEnabled ? (
                    <button
                      type="button"
                      onClick={() => setEditEmailEnabled(true)}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {t('admin.change_email', 'Change Email')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setEditEmailEnabled(false); setEditEmail(editingUser?.email || ''); }}
                      className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                    >
                      Cancel
                    </button>
                  )}
                </div>
                {!editEmailEnabled ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg">
                    {editingUser?.email}
                  </p>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      placeholder="new@email.com"
                      className="w-full px-3 py-2 border border-amber-300 dark:border-amber-600 rounded-lg text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 dark:bg-gray-700 dark:text-white bg-amber-50"
                    />
                    <input
                      type="text"
                      value={editEmailReason}
                      onChange={(e) => setEditEmailReason(e.target.value)}
                      placeholder="Reason for change (optional, for audit)"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-xs"
                    />
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Changing email will reset email verification and send a notification to the new address.
                    </p>
                  </div>
                )}
              </div>

              {/* Two-Factor Authentication — accepted users only. Pending
                  invitations set 2FA via the invite-time exemption instead;
                  portal members without an activated account have no login,
                  hence nothing to require 2FA on. */}
              {editingUser.user_type !== 'invited' &&
                !(typeof editingUser.id === 'number' && editingUser.id < 0) &&
                getAccountUserId(editingUser) !== null && (
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.two_factor', 'Two-Factor Authentication (2FA)')}
                  </label>
                  {editTwoFAInitial === null ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Loading 2FA status…</p>
                  ) : (
                    <>
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editTwoFARequired}
                          onChange={(e) => setEditTwoFARequired(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>
                          <span className="text-sm text-gray-700 dark:text-gray-200">
                            Require 2FA at login for this user
                          </span>
                          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                            Unchecking exempts this user from 2FA — they are never
                            prompted for a code, even if your organization enforces
                            2FA org-wide.
                          </span>
                        </span>
                      </label>
                      {editTwoFARequired && (
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Method:</span>
                          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setEditTwoFAMethod('sms')}
                              className={`px-3 py-1 text-xs font-medium ${editTwoFAMethod === 'sms' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                            >
                              SMS
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditTwoFAMethod('totp')}
                              className={`px-3 py-1 text-xs font-medium ${editTwoFAMethod === 'totp' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                            >
                              Authenticator (TOTP)
                            </button>
                          </div>
                        </div>
                      )}
                      {editTwoFARequired && editTwoFAMethod === 'sms' && !editTwoFAPhoneVerified && (
                        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                          Note: SMS 2FA needs a verified phone number for this user,
                          or they won&apos;t receive login codes.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setEditingUser(null)}
                disabled={editSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('admin.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleSaveEditUser}
                disabled={editSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {editSaving ? t('admin.saving', 'Saving...') : t('admin.save_changes', 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BUG-005: Edit invitation role modal */}
      {editingInvitation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingInvitation.status === 'held' ? 'Edit held invitation' : 'Edit invitation'}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Changes apply before the invitee accepts.
              </p>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editInvName}
                  onChange={(e) => setEditInvName(e.target.value)}
                  disabled={editInvSaving}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={editInvEmail}
                  onChange={(e) => setEditInvEmail(e.target.value)}
                  disabled={editInvSaving}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone <span className="text-gray-400 font-normal">(for 2FA, optional)</span>
                </label>
                <input
                  type="tel"
                  value={editInvPhone}
                  onChange={(e) => setEditInvPhone(e.target.value)}
                  placeholder="+18085551234"
                  disabled={editInvSaving}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Role
                </label>
                <select
                  value={editInvRole}
                  onChange={(e) => setEditInvRole(e.target.value)}
                  disabled={editInvSaving}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  {/* TRUS-186: on WeaveIn, offer the org's real roles
                      (role_terminology role_type -> display_name_en) so custom
                      roles like "Case Manager" are selectable and the current
                      role is preserved. Triveny — and WeaveIn when org roles
                      failed to load — fall back to the hardcoded system-role
                      keys from USER_ROLES.
                      - ops_admin is ALWAYS excluded on WeaveIn (platform-internal).
                      - super_admin / ops_admin / platform_admin tiers only for
                        super_admin callers; backend gates the rest with 403. */}
                  {(() => {
                    if (!isTriveny && orgRoles.length > 0) {
                      // TRUS-513: internal tiers are never offered here, for
                      // ANYBODY — the `isSuperAdmin ||` escape below used to let
                      // a super admin pick them in any org. Every org carries a
                      // seeded role_terminology row with system_role='super_admin'
                      // (historical; the seeder no longer creates them, and they
                      // must stay active because nav config keys its overrides to
                      // them), so that escape surfaced "Super Admin" in every
                      // org's dropdown — which is how it showed up in Somos.
                      // The backend now rejects a role edit to super_admin
                      // outright, so offering it could only ever produce a 400.
                      const visible = orgRoles.filter(
                        (r) => !isInternalSystemRole(r.system_role) && r.system_role !== 'platform_admin',
                      );
                      // Guarantee the invitation's current role is always an
                      // option so the <select> shows it and never silently snaps
                      // to the first entry (the "defaults to Super Admin" bug).
                      const currentRole = editingInvitation.role;
                      if (currentRole && !visible.some((r) => r.role_type === currentRole)) {
                        visible.unshift({
                          role_type: currentRole,
                          display_name_en:
                            editingInvitation.role_names ||
                            USER_ROLES[currentRole as keyof typeof USER_ROLES]?.label ||
                            currentRole,
                          system_role: currentRole,
                        });
                      }
                      return visible.map((r) => (
                        <option key={r.role_type} value={r.role_type}>
                          {r.display_name_en || r.role_type}
                        </option>
                      ));
                    }
                    return Object.entries(USER_ROLES).map(([key, info]) => {
                      const TRIVENY_KEYS = new Set<string>([
                        'tenant_admin', 'tenant_user', 'tenant_viewer',
                      ]);
                      if (currentUserRole === 'platform_admin' || currentUserRole === 'super_admin') {
                        TRIVENY_KEYS.add('platform_admin');
                      }
                      if (isTriveny && !TRIVENY_KEYS.has(key)) return null;
                      if (!isTriveny && (
                        key === 'platform_admin' ||
                        key === 'tenant_admin' ||
                        key === 'tenant_user' ||
                        key === 'tenant_viewer'
                      )) return null;
                      if (key === 'ops_admin') return null;
                      // TRUS-513: not offered on the WeaveIn side at all. Granting
                      // super_admin has exactly one entry point — a fresh Platform
                      // Admin invitation in CBO 1, which parks for a second approver.
                      if (key === 'super_admin' && (!isTriveny || currentUserRole !== 'super_admin')) return null;
                      return (
                        <option key={key} value={key}>
                          {info.label}
                        </option>
                      );
                    });
                  })()}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Current: <strong>{editingInvitation.role_names || USER_ROLES[editingInvitation.role as keyof typeof USER_ROLES]?.label || editingInvitation.role}</strong>
                </p>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editInvRequire2FA}
                    onChange={(e) => setEditInvRequire2FA(e.target.checked)}
                    disabled={editInvSaving}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                  />
                  <span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Require two-factor authentication (2FA)
                    </span>
                    <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                      Unchecking exempts this user from 2FA when they register —
                      they are never prompted for a code, even if your organization
                      enforces 2FA org-wide.
                    </span>
                  </span>
                </label>
              </div>
              {editInvError && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded px-3 py-2">
                  {editInvError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setEditingInvitation(null)}
                disabled={editInvSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveInvitationRole}
                disabled={editInvSaving || !editInvRole || !editInvEmail.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {editInvSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
